import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy, faDownload, faQrcode } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { formatCurrency as formatCurrencyFull } from '../../utils/helpers';
import { buildTransferNote, buildTransferQr, transferQrPng } from '../../utils/vietQr';
import { BankInfo } from '../../types';

const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

interface TransferQrProps {
    bankInfo: BankInfo;
    amount: number;
    payerName: string;
    eventName?: string;
}

/**
 * The transfer QR inside a settlement card: a VietQR that already carries the
 * exact amount and a note naming the payer and the event, so the recipient can
 * tell one incoming transfer from another without asking.
 *
 * The payer usually opens this page on the very phone they will pay from, so
 * they cannot scan a code on their own screen. "Save QR image" puts it in the
 * photo library to open from inside the banking app (Scan QR -> pick image) —
 * that works with every bank, unlike deep links, which only a few banks fill the
 * amount in for.
 *
 * When the bank name in the profile is not one this app knows a BIN for, the
 * recipient's own uploaded QR image is shown instead — that one has no amount in
 * it, which is exactly what it always was.
 */
const TransferQr: React.FC<TransferQrProps> = ({ bankInfo, amount, payerName, eventName }) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    const [file, setFile] = useState<File | null>(null);
    const [noteCopied, setNoteCopied] = useState(false);

    const note = useMemo(() => buildTransferNote(payerName, eventName), [payerName, eventName]);
    const qr = useMemo(
        () => (amount > 0 ? buildTransferQr(bankInfo, amount, note) : null),
        [bankInfo, amount, note]
    );

    // Draw the image ahead of the tap: Safari only opens the share sheet inside
    // the gesture itself, so awaiting the draw first gets it blocked.
    const accountHolder = bankInfo.accountHolder;
    useEffect(() => {
        setFile(null);
        if (!qr) return;
        let cancelled = false;
        // The saved image is read on its own, away from the event's compact-mode
        // setting, so it spells the amount out in full rather than as "350K".
        transferQrPng(qr.payload, [accountHolder, `${formatCurrencyFull(amount)} · ${qr.note}`])
            .then(blob => {
                if (cancelled) return;
                const name = `transfer-${qr.note.replace(/[^A-Z0-9]+/g, '-').toLowerCase() || 'qr'}.png`;
                setFile(new File([blob], name, { type: 'image/png' }));
            })
            .catch(error => console.error('Error drawing transfer QR:', error));
        return () => {
            cancelled = true;
        };
    }, [qr, amount, accountHolder]);

    const handleCopyNote = async () => {
        if (!qr?.note) return;
        try {
            await navigator.clipboard.writeText(qr.note);
            setNoteCopied(true);
            setTimeout(() => setNoteCopied(false), 2000);
        } catch (error) {
            console.error('Error copying transfer note:', error);
        }
    };

    const handleSaveQr = async () => {
        if (!file) return;
        try {
            if (isIOS() && navigator.canShare?.({ files: [file] })) {
                try {
                    await navigator.share({ files: [file] });
                } catch (error) {
                    // The share sheet was dismissed — not a failure.
                    if ((error as Error)?.name !== 'AbortError') throw error;
                }
                return;
            }

            const url = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            console.error('Error saving transfer QR:', error);
        }
    };

    // No QR when the bank is not on the VietQR list — the picker in the profile
    // only offers banks that are, so this is a profile saved before it existed.
    if (!qr) return null;

    return (
        <div className="pt-1 flex flex-col items-center gap-1.5">
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faQrcode} className="text-orange-600 dark:text-orange-400" aria-hidden="true"/>
                {t('event.payments.transferTo.qrTitle')}
            </div>

            <img
                src={qr.dataUrl}
                alt={t('event.payments.transferTo.qrTitle')}
                className="w-40 h-40 rounded-lg bg-white p-1 border border-gray-200 dark:border-gray-600"
            />

            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
                {t('event.payments.transferTo.qrHint', { amount: formatCurrency(amount) })}
            </p>

            {qr.note && (
                <div className="flex items-center gap-2 text-xs w-full">
                    <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {t('event.payments.transferTo.note')}
                    </span>
                    <span className="font-mono font-semibold text-gray-900 dark:text-white break-all">
                        {qr.note}
                    </span>
                    <button
                        type="button"
                        onClick={handleCopyNote}
                        className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                        title={t('event.payments.transferTo.copyNote')}
                        aria-label={t('event.payments.transferTo.copyNote')}
                    >
                        <FontAwesomeIcon
                            icon={noteCopied ? faCheck : faCopy}
                            className={noteCopied ? 'text-green-600 dark:text-green-400' : ''}
                            aria-hidden="true"
                        />
                        {noteCopied && (
                            <span className="text-green-600 dark:text-green-400">
                                {t('event.payments.transferTo.copied')}
                            </span>
                        )}
                    </button>
                </div>
            )}

            <button
                type="button"
                onClick={handleSaveQr}
                disabled={!file}
                className="flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
                <FontAwesomeIcon icon={faDownload} aria-hidden="true"/>
                {t('event.payments.transferTo.saveQr')}
            </button>

            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
                {t('event.payments.transferTo.saveQrHint')}
            </p>
        </div>
    );
};

export default TransferQr;
